import { ExclamationCircleOutlined, LoadingOutlined, RedoOutlined } from '@ant-design/icons';
import { Alert, Button, Flex, Input, Space, Tag, Typography } from 'antd';
import { ArtifactEditorMode } from 'components/Artifacts/editorTypes';
import { ArtifactTranscript } from 'components/Artifacts/types';

const { Paragraph, Text } = Typography;

type ArtifactTranscriptPanelProps = {
  mode: ArtifactEditorMode;
  transcript: ArtifactTranscript;
  value: string;
  onChange: (value: string) => void;
  onRetry?: () => void;
  isRetrying?: boolean;
};

const getTranscriptStatusTone = (status: ArtifactTranscript['status']) => {
  switch (status) {
    case 'COMPLETED':
      return { color: 'success', label: 'Completed' } as const;
    case 'FAILED':
      return { color: 'error', label: 'Failed' } as const;
    case 'PROCESSING':
      return { color: 'processing', label: 'Processing' } as const;
    case 'QUEUED':
      return { color: 'warning', label: 'Queued' } as const;
    default:
      return { color: 'default', label: 'Not requested' } as const;
  }
};

const sectionBadgeStyle = {
  alignItems: 'center',
  background: 'linear-gradient(180deg, #7c6cff 0%, #5f4bf4 100%)',
  borderRadius: '999px',
  color: '#fff',
  display: 'inline-flex',
  fontSize: 12,
  fontWeight: 700,
  height: 20,
  justifyContent: 'center',
  minWidth: 20
} as const;

export function ArtifactTranscriptPanel({
  mode,
  transcript,
  value,
  onChange,
  onRetry,
  isRetrying = false
}: ArtifactTranscriptPanelProps) {
  if (mode !== 'edit') {
    return null;
  }

  const hasTranscriptData = Boolean(
    transcript.text || transcript.generatedText || transcript.errorCode || transcript.status !== 'NOT_REQUESTED'
  );

  if (!hasTranscriptData) {
    return null;
  }

  const statusTone = getTranscriptStatusTone(transcript.status);

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #f1f2f8',
        borderRadius: 16,
        padding: 16
      }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Flex align="center" gap={8} wrap="wrap">
          <span style={sectionBadgeStyle}>5</span>
          <Text style={{ color: '#26233a', fontSize: 15, fontWeight: 600 }}>Transcript</Text>
          <Text style={{ color: '#9aa0b4', fontSize: 13, fontWeight: 500 }}>(auto-generated)</Text>
          <Tag
            color={statusTone.color}
            icon={transcript.status === 'PROCESSING' ? <LoadingOutlined /> : undefined}
            style={{ borderRadius: 999, marginInlineStart: 'auto' }}
          >
            {transcript.status === 'COMPLETED' ? 'Transcript ready' : statusTone.label}
          </Tag>
        </Flex>

        <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: -4 }}>
          Transcript correction stays separate from the written description.
        </Paragraph>

        {transcript.status === 'FAILED' && (
          <Alert
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined />}
            message={transcript.errorCode || 'Transcription failed. You can retry after saving or reviewing the audio.'}
          />
        )}

        {transcript.generatedText && (
          <Alert type="info" showIcon message={transcript.generatedText} description="Latest generated transcript" />
        )}

        {(transcript.canRetry || transcript.status === 'FAILED') && onRetry && (
          <Button icon={<RedoOutlined />} onClick={onRetry} loading={isRetrying} style={{ borderRadius: 12, width: 'fit-content' }}>
            Retry transcription
          </Button>
        )}

        {(transcript.text || transcript.generatedText) && (
          <Input.TextArea
            rows={5}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder="Correct the transcript here without changing the written description."
            style={{ borderColor: '#e7e8f3', borderRadius: 12, paddingTop: 12, resize: 'none' }}
          />
        )}
      </Space>
    </div>
  );
}

export default ArtifactTranscriptPanel;